// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import React from 'react'
import { AutoComplete, Button, Row, Col, message, Input } from 'antd'
import { fuzzySearchDeviceId } from 'api/devices'
import { debounce } from 'lodash'
import { DeviceStatus, OnlineStatus } from 'constants/device'

export type ISearchHeaderProps = {
  onSearch: (value: string) => void
  filterStatus: {
    status: DeviceStatus
    onlineStatus: OnlineStatus
    cityID: number
  }
  disabled: boolean
}

export interface ISearchHeaderState {
  options: { label: string; value: string }[]
  keyword: string
}

export class SearchHeader extends React.Component<ISearchHeaderProps, ISearchHeaderState> {
  state = {
    options: [],
    keyword: null
  }

  componentWillUnmount () {
    this.setState({
      options: [],
      keyword: null
    })
  }

  private generateSearchOptions = async (value: string) => {
    const { status, onlineStatus, cityID } = this.props.filterStatus
    if (!value || value.trim() === '') {
      this.setState({
        options: []
      })
      return
    }
    let ids
    const query = {
      keyword: value,
      cityID,
      status,
      onlineStatus,
    }
    // used to be -1
    if (status === 0) {
      delete query.status
    }
    // used to be -1
    if (onlineStatus === 0) {
      delete query.onlineStatus
    }
    try {
      ids = await fuzzySearchDeviceId(query)
    } catch (error) {
      console.log(error)
      if (error.response) {
        message.error({ title: error.response.status, content: error.response.data })
      }
    }
    if (!ids) {
      this.setState({
        options: []
      })
      return
    }
    const options = ids.map(d => ({
      label: d,
      value: d
    }))
    this.setState({
      options
    })
  }

  private handleDeviceIDSeachDebounce = debounce(this.generateSearchOptions, 200)

  private handleSearch = (value, e) => {
    e.preventDefault()
    this.props.onSearch(this.state.keyword)
  }

  private handleOptionSelect = (value: string) => {
    this.props.onSearch(value)
  }

  private handleInputChange = (value: string) => {
    this.setState({
      keyword: value === undefined ? null : value
    })
  }

  private handleClear = () => {
    this.setState({
      keyword: null
    })
    this.props.onSearch(null)
  }

  render () {
    const {disabled} = this.props
    return (
      <Row className='camera-finder-header' justify='space-between'>
        <Col style={{ marginRight: 4 }} flex={1}>
          <AutoComplete
            onSelect={this.handleOptionSelect}
            onSearch={this.handleDeviceIDSeachDebounce}
            onChange={this.handleInputChange}
            options={this.state.options}
            value={this.state.keyword}
            className='camera-finder-header-auto-complete'
            disabled={disabled}
          >
            <Input.Search onSearch={this.handleSearch} enterButton='Search' placeholder='Search by CameraID' />
          </AutoComplete>
        </Col>
        <Col>
          <Button onClick={this.handleClear} disabled={disabled}>Clear</Button>
        </Col>
      </Row>
    )
  }
}
